import React, { useRef, useEffect, useMemo } from "react";
import { Sprite, Container } from "@pixi/react";
import { useButtonHover } from "../../../../Hooks/useButtonHover";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toggleFullscreen } from "../../../../State/actions";
import { SOUND_NAME } from "../../../../sounds";
import { getUITexture } from "../../../../assets";
import { isIOS } from "../../../../utilities";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PRIMARY_USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";

// Position config — extracted for easy tweaking
const FULLSCREEN_BUTTON_POSITION_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.0,
      y: (toolbar) => toolbar.height / 2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.0,
      y: (toolbar) => toolbar.height / 2,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.0,
      y: (toolbar) => toolbar.height / 2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.0,
      y: (toolbar) => toolbar.height / 2,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width,
    y: (toolbar) => toolbar.height / 2,
  },
};

const ButtonFullscreen = ({ toolbar }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { fullscreenEnabled, fullscreenSupported } = useSelector(
    (state) => state.settings
  );
  const { getResponsiveConfig, device, orientation } = useDimensions();

  const { hoverContainerRef, buttonRef, handlePointerOver, handlePointerOut, triggerPress } = useButtonHover();
  const isProcessingClickRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  const iconName = fullscreenEnabled
    ? "icon_fullscreen_disable"
    : "icon_fullscreen_enable";
  const texture = getUITexture(iconName);

  const buttonConfig = useMemo(
    () => getResponsiveConfig(PRIMARY_USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  // Position using config
  const buttonPosition = useMemo(() => {
    const deviceConfig = FULLSCREEN_BUTTON_POSITION_CONFIG[device];
    const posConfig = deviceConfig?.[orientation] ?? deviceConfig;
    return {
      x: posConfig.x(toolbar, buttonConfig),
      y: posConfig.y(toolbar, buttonConfig),
    };
  }, [device, orientation, toolbar, buttonConfig]);

  const handleClick = () => {
    if (isProcessingClickRef.current) {
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);

    triggerPress();

    dispatch(toggleFullscreen());

    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);



  if (isIOS() || !fullscreenSupported) {
    return null;
  }

  return (
    <Container
      name="secondary_button_fullscreen"
      x={buttonPosition.x}
      y={buttonPosition.y}
      zIndex={2}
    >
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={buttonRef}
          texture={texture}
          name="button_fullscreen_icon"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode="static"
          cursor="pointer"
          onpointertap={handleClick}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          accessible={true}
          accessibleTitle={
            fullscreenEnabled ? t("disable_fullscreen", { ns: "common" }) : t("enable_fullscreen", { ns: "common" })
          }
          accessibleHint={
            fullscreenEnabled ? t("disable_fullscreen", { ns: "common" }) : t("enable_fullscreen", { ns: "common" })
          }
          resolution={2}
        />
      </Container>
    </Container>
  );
};

export default ButtonFullscreen;
